/**
 * Honest, organisation-derived marketing SEO defaults.
 *
 * Multi-tenant rule: a brand-new organisation must NEVER inherit another
 * brand's specific claims (ratings, review counts, years in business, prices,
 * city). These helpers build a sensible default homepage title/description from
 * ONLY the facts the organisation actually gave us (name, tagline, city/region).
 * No numbers, ratings or history are ever invented.
 */

export type OrgSeoInputs = {
  companyName?: string | null;
  tagline?: string | null;
  city?: string | null;
  region?: string | null;
};

export const ORG_SEO_TITLE_MAX = 60;
export const ORG_SEO_DESCRIPTION_MAX = 160;

function clean(value?: string | null): string {
  return (value ?? '').replace(/\s+/g, ' ').trim();
}

function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  const sliced = value.slice(0, max - 1).trimEnd();
  return `${sliced}…`;
}

function resolvePlace(inputs: OrgSeoInputs): string {
  const city = clean(inputs.city);
  const region = clean(inputs.region);
  if (city && region && region.toLowerCase() !== city.toLowerCase()) {
    return `${city}, ${region}`;
  }
  return city || region;
}

/**
 * Build an honest homepage SEO title from org facts only.
 * Pattern: "Company | City | Tagline", trimmed to fit search limits.
 */
export function buildOrgHomepageSeoTitle(inputs: OrgSeoInputs): string {
  const name = clean(inputs.companyName) || 'Your Business';
  const tagline = clean(inputs.tagline);
  const place = resolvePlace(inputs);

  const parts = [name];
  if (place) parts.push(place);
  if (tagline) parts.push(tagline);

  const full = parts.join(' | ');
  if (full.length <= ORG_SEO_TITLE_MAX) return full;

  // Drop the tagline first, then truncate as a last resort.
  const withoutTagline = place ? `${name} | ${place}` : name;
  if (withoutTagline.length <= ORG_SEO_TITLE_MAX) return withoutTagline;
  return truncate(name, ORG_SEO_TITLE_MAX);
}

/**
 * Build an honest homepage meta description from org facts only.
 * No fabricated ratings, counts, years or prices.
 */
export function buildOrgHomepageSeoDescription(inputs: OrgSeoInputs): string {
  const name = clean(inputs.companyName) || 'Your Business';
  const tagline = clean(inputs.tagline);
  const place = resolvePlace(inputs);

  const sentences: string[] = [];
  if (tagline) {
    sentences.push(/[.!?]$/.test(tagline) ? tagline : `${tagline}.`);
  }
  sentences.push(
    place
      ? `${name} provides professional services in ${place}.`
      : `${name} provides professional services for homes and businesses.`,
  );
  sentences.push('Book online or get in touch for a quick quote.');

  return truncate(sentences.join(' '), ORG_SEO_DESCRIPTION_MAX);
}

export function buildOrgHomepageSeoDefault(inputs: OrgSeoInputs): { title: string; description: string } {
  return {
    title: buildOrgHomepageSeoTitle(inputs),
    description: buildOrgHomepageSeoDescription(inputs),
  };
}
